import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/lib/config/routes';

const STALE_CHUNK_RELOAD_KEY = 'dbscaffolding:stale-chunk-reload';

const isChunkError = (detail: string) =>
  /dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported/i.test(
    detail,
  );

export function RouteError() {
  const error = useRouteError();

  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  const chunkError = isChunkError(detail);

  if (chunkError && !sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
    window.location.reload();
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-content">
          {status === 404 ? 'Page not found' : 'Something went wrong'}
        </h1>
        <p className="mt-2 text-sm text-content-muted">
          {chunkError
            ? 'This page failed to load — a newer version of the app may be available. Reloading usually fixes it.'
            : status === 404
              ? "The page you're looking for doesn't exist or was moved."
              : 'An unexpected error occurred while loading this page.'}
        </p>
        {detail && (
          <p className="mt-3 break-words rounded-lg bg-surface-muted px-3 py-2 text-left text-xs text-content-subtle">
            {detail}
          </p>
        )}
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button icon={<RefreshCw className="h-4 w-4" />} onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button
            variant="ghost"
            icon={<Home className="h-4 w-4" />}
            onClick={() => window.location.assign(ROUTES.dashboard)}
          >
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}
